import Vue from 'vue';
import { css } from '@emotion/css';
export { css, keyframes } from '@emotion/css';

const classResolver = ({
  theme,
  stylesTemplates,
  props,
  staticClass
}) => {
  const classes = stylesTemplates.map(stylesTemplate => {
    const [strings, tags] = stylesTemplate;
    let flatTags = [];

    if (tags !== null && tags !== void 0 && tags.length) {
      flatTags = tags.map(item => {
        if (typeof item === 'function') {
          return item(props, theme);
        }

        return item;
      });
    }

    return css(strings, ...flatTags);
  });
  const genClass = `${classes.join(' ')}${staticClass ? ` ${staticClass}` : ''}`;
  return genClass;
};

const compGen = (htmlTag, stylesTemplates, propTypes = {}) => Vue.component('StyledComponent', {
  props: {
    className: String,
    value: [String, Number, Date, Boolean],
    ...propTypes
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      },

      set(newVal) {
        this.$emit('input', newVal);
      }

    }
  },

  render(createElement) {
    var _this$$slots;

    return createElement(htmlTag, {
      class: (() => {
        let theme = {};

        if (this.$veTheme) {
          theme = this.$veTheme.get();
        }

        const resolvedClasses = classResolver({
          theme,
          stylesTemplates,
          props: this.$props,
          staticClass: this.$vnode.data.staticClass
        });
        this.$vnode.data.staticClass = '';
        return resolvedClasses;
      })(),
      on: { ...this.$listeners,
        input: newVal => {
          this.localValue = newVal.target.value;
        }
      },
      domProps: {
        value: this.localValue
      }
    }, (_this$$slots = this.$slots) === null || _this$$slots === void 0 ? void 0 : _this$$slots.default);
  },

  styledComponent: {
    stylesTemplates,
    htmlTag,
    propTypes
  }
});
const styledComp = (htmlTag, stylesTemplate) => propTypes => compGen(htmlTag, [stylesTemplate], propTypes);

const funcGen = (htmlTag, stylesTemplates, propTypes = {}) => Vue.component('StyledComponent', {
  functional: true,
  props: {
    className: String,
    ...propTypes
  },

  render(createElement, context) {
    const {
      children,
      data,
      props,
      parent
    } = context;
    return createElement(htmlTag, { ...data,
      class: (() => {
        let theme = {};

        if (parent.$veTheme) {
          theme = parent.$veTheme.get();
        }

        const resolvedClasses = classResolver({
          theme,
          stylesTemplates,
          props,
          staticClass: data.staticClass
        });
        data.staticClass = '';
        return resolvedClasses;
      })(),
      on: { ...data.on
      },
      attrs: { ...data.attrs
      }
    }, children);
  },

  styledComponent: {
    functional: true,
    stylesTemplates,
    htmlTag,
    propTypes
  }
});
const styledFunc = (htmlTag, stylesTemplate) => propTypes => funcGen(htmlTag, [stylesTemplate], propTypes);

var domElements = ['a', 'abbr', 'address', 'area', 'article', 'aside', 'audio', 'b', 'base', 'bdi', 'bdo', 'big', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'cite', 'code', 'col', 'colgroup', 'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt', 'em', 'embed', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'iframe', 'img', 'input', 'ins', 'kbd', 'keygen', 'label', 'legend', 'li', 'link', 'main', 'map', 'mark', 'menu', 'menuitem', 'meta', 'meter', 'nav', 'noscript', 'object', 'ol', 'optgroup', 'option', 'output', 'p', 'param', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'script', 'section', 'select', 'small', 'source', 'span', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time', 'title', 'tr', 'track', 'u', 'ul', 'var', 'video', 'wbr', // SVG
'circle', 'clipPath', 'defs', 'ellipse', 'foreignObject', 'g', 'image', 'line', 'linearGradient', 'marker', 'mask', 'path', 'pattern', 'polygon', 'polyline', 'radialGradient', 'rect', 'stop', 'svg', 'text', 'tspan'];

var inputElements = ['input', 'select', 'textarea'];

const styled = styledInstance => {
  const {
    htmlTag,
    stylesTemplates: formerStyles,
    propTypes: formerPropTypes,
    functional
  } = styledInstance.extendOptions.styledComponent;

  if (functional) {
    return (strings, ...tags) => propTypes => funcGen(htmlTag, [...formerStyles, [strings, tags]], { ...formerPropTypes,
      ...propTypes
    });
  }

  return (strings, ...tags) => propTypes => compGen(htmlTag, [...formerStyles, [strings, tags]], { ...formerPropTypes,
    ...propTypes
  });
};

domElements.forEach(elem => {
  styled[elem] = (strings, ...tags) => styledFunc(elem, [strings, tags]);
}); // input elements need to be components so v-model would still work

inputElements.forEach(elem => {
  styled[elem] = (strings, ...tags) => styledComp(elem, [strings, tags]);
});

const VEGlobalTheme = (Vue, theme) => {
  const veTheme = Vue.observable({
    theme: { ...theme
    }
  });
  Vue.prototype.$veTheme = {
    get: () => veTheme.theme,
    set: val => {
      veTheme.theme = val;
    }
  };
};

var GlobalThemeComponent = Vue.component('VEGlobalTheme', {
  props: {
    theme: {
      type: Object,
      required: true
    }
  },
  watch: {
    theme(newVal) {
      this.calcTheme(newVal);
    }

  },
  computed: {
    rootId() {
      return this.$root.$el.id;
    },

    stylesId() {
      return `styled-vars-${this.rootId}`;
    }

  },
  methods: {
    calcTheme(themeObj) {
      if (this.$veTheme) {
        this.$veTheme.set(themeObj);
      }

      const themeKeys = Object.keys(themeObj);
      let styleString = themeKeys.reduce((acc, key) => {
        let res = acc;
        res += `--${key}: ${themeObj[key]};\n`;
        return res;
      }, `#${this.rootId} {\n`);
      styleString += '}';
      document.querySelector(`#${this.stylesId}`).innerHTML = styleString;
    }

  },

  mounted() {
    const styleElem = document.createElement('style');
    styleElem.setAttribute('id', this.stylesId);
    document.head.appendChild(styleElem);
    this.calcTheme(this.theme);
  },

  render(createElement) {
    // making sure multi children are possible wrapping the slots with
    // a single div applied "display: contents" as style
    return createElement('div', {
      domProps: {
        style: 'display: contents'
      }
    }, this.$slots.default);
  }

});

export default styled;
export { VEGlobalTheme, GlobalThemeComponent as VEGlobalThemeComponent };
